import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import {
  DocumentAttachment,
  DocumentUploadComponent,
  SidebarNavClickEvent,
  SidebarNavComponent,
  SidebarNavItem,
} from '@sportello-balneari/shared-components';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { Observable, of } from 'rxjs';
import { CompilingApiService } from '../../../api/compiling-api.service';
import {
  isSectionStructureObject,
  isSubSectionStructureObject,
} from '../../../api/interfaces';
import { CompilingSectionComponent } from '../../../components/tile/compiler/section/section.component';
import { CompilingFormService } from '../../../services/compiling/compiling-form.service';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';
import { AnswerAttachmentsService } from '../../../services/compiling/state/answer-attachments.service';
import { AnswerPermissionsService } from '../../../services/compiling/state/answer-permissions.service';
import { CompilingDashboardService } from '../compiling-dashboard.service';
import { EnrichedSlotMetaComponent } from '../enriched-slot-meta.component';
import { PremessaPanelComponent } from './premessa-panel/premessa-panel.component';
import { BulkCompileButtonComponent } from '../bulk-compile/bulk-compile-button.component';
import {
  type EnrichedSlot,
  requirementsToSlots,
} from '../requirements-to-slots';
import {
  deleteAttachmentFn,
  migrateUnknownAttachments,
  reindexAttachmentFn,
  uploadAndIndexFn,
} from '../utils';

const DOCUMENTS_ID = 'documents';
const PREMESSA_ID = 'premessa';

@Component({
  selector: 'lib-envelop-b',
  template: `
    @let module = forms.moduleStructure();
    @let ready = forms.ready();
    @if (ready && module) {
      <div
        class="mx-auto grid w-full max-w-screen-lg grid-cols-[20rem_1fr] items-start gap-4"
      >
        <aside
          class="sticky top-[120.250px] flex max-h-[calc(100vh-220px)] flex-shrink-0 flex-col overflow-y-auto rounded-2xl border border-gray-200 bg-white"
        >
          <!-- Static nav (Documenti) -->

          <lib-sidebar-nav
            data-tour="envelope-b-static-nav"
            [items]="staticNavItems()"
            [activeId]="activeNavId()"
            (itemClick)="onNavClick($event)"
          />

          <hr class="my-2 border-gray-100" />

          <lib-sidebar-nav
            [items]="navItems()"
            [activeId]="activeNavId()"
            (itemClick)="onNavClick($event)"
          />
        </aside>

        <!-- Main content area -->
        <div class="flex flex-col overflow-y-auto">
          <lib-bulk-compile-button class="mb-2 block w-full" />

          @if (activeView() === 'documents') {
            <div class="flex flex-col gap-6" data-tour="envelope-b-documents">
              @for (item of enrichedDocumentSlots(); track item.slot.id) {
                <lib-document-upload
                  [slot]="item.slot"
                  [uploadFn]="uploadFn(item.slot.id)"
                  [deleteFn]="deleteFn"
                  [reindexFn]="reindexFn"
                  [readonly]="!permissions.canEditTechnicalOffer()"
                  [value]="getSlot(item.slot.id)"
                  (valueChange)="setSlot(item.slot.id, $event)"
                >
                  <lib-enriched-slot-meta [item]="item" />
                </lib-document-upload>
              }
            </div>
          } @else if (activeView() === 'premessa') {
            <lib-premessa-panel [tenderFormAnswerId]="tenderFormAnswerId()" />
          } @else {
            @let currentSectionId = this.sectionId();
            @if (currentSectionId) {
              <div
                class="flex w-full flex-col gap-2 rounded-2xl border border-gray-200 bg-white"
              >
                <lib-compiling-section [uniqueId]="currentSectionId" />
                <p-paginator
                  (onPageChange)="onPageChange($event)"
                  [first]="first()"
                  [rows]="1"
                  [totalRecords]="module.root.children.length"
                  [showCurrentPageReport]="true"
                  [showPageLinks]="false"
                  [showJumpToPageDropdown]="false"
                  [showFirstLastIcon]="false"
                  currentPageReportTemplate="Sezione {first} di {totalRecords}"
                  class="rounded-2xl"
                />
              </div>
            }
          }
        </div>
      </div>
    }
  `,
  imports: [
    Paginator,
    SidebarNavComponent,
    CompilingSectionComponent,
    DocumentUploadComponent,
    EnrichedSlotMetaComponent,
    PremessaPanelComponent,
    BulkCompileButtonComponent,
  ],
})
export class EnvelopBComponent {
  protected readonly forms = inject(CompilingFormService);
  protected readonly tenderContext = inject(TenderContextService);
  protected readonly attachments = inject(AnswerAttachmentsService);
  protected readonly compilingApiService = inject(CompilingApiService);
  protected readonly permissions = inject(AnswerPermissionsService);
  protected readonly compilingDashboardService = inject(
    CompilingDashboardService,
    { optional: true },
  );

  first = signal<number>(0);
  readonly activeView = signal<'form' | 'documents' | 'premessa'>('form');

  sectionId = computed(() => {
    const module = this.forms.moduleStructure();
    if (!module) return undefined;
    return module.root.children[this.first()];
  });

  readonly tenderFormAnswerId = computed(
    () => this.tenderContext.tenderFormAnswer()?.id ?? '',
  );

  readonly activeNavId = computed(() => {
    if (this.activeView() === 'documents') return DOCUMENTS_ID;
    if (this.activeView() === 'premessa') return PREMESSA_ID;
    return this.sectionId() ?? null;
  });

  readonly attachmentCount = computed(() =>
    Object.values(this.attachments.envelopeBAttachments()).reduce(
      (sum, files) => sum + files.length,
      0,
    ),
  );

  readonly staticNavItems = computed<SidebarNavItem[]>(() => [
    {
      id: DOCUMENTS_ID,
      label: 'Documenti',
      icon: 'pi pi-file',
      badge: this.attachmentCount() || undefined,
    },
    {
      id: PREMESSA_ID,
      label: 'Relazione di Gestione',
      icon: 'pi pi-file-word',
    },
  ]);

  private static readonly DEFAULT_ENRICHED_SLOTS: EnrichedSlot[] = [
    {
      slot: { id: 'offerta-tecnica', label: 'Offerta tecnica', multiple: true },
      members: [],
    },
    {
      slot: {
        id: 'altri-documenti',
        label: 'Altri documenti (p. es. Preventivi)',
        multiple: true,
      },
      members: [],
    },
  ];

  protected readonly enrichedDocumentSlots = computed<EnrichedSlot[]>(() => {
    const tenderInfo = this.tenderContext.tenderInfo();
    const envelopeB = tenderInfo?.extractedRequirements?.envelopeB;
    if (!envelopeB?.length) return EnvelopBComponent.DEFAULT_ENRICHED_SLOTS;

    return [
      ...requirementsToSlots(envelopeB, tenderInfo?.attachments ?? []),
      {
        slot: {
          id: 'altri-documenti',
          label: 'Altri documenti',
          multiple: true,
        },
        members: [],
      },
    ];
  });

  protected readonly uploadFn = uploadAndIndexFn(
    this.compilingApiService,
    'ENVELOPE_B',
    () => this.tenderContext.tenderFormAnswer()?.id ?? '',
  );

  protected readonly deleteFn = deleteAttachmentFn(this.compilingApiService);
  protected readonly reindexFn = reindexAttachmentFn(this.compilingApiService);

  private readonly knownSlotIds = computed(
    () => new Set(this.enrichedDocumentSlots().map((item) => item.slot.id)),
  );

  constructor() {
    migrateUnknownAttachments(
      this.attachments.envelopeBAttachments,
      this.knownSlotIds,
    );

    // Someone (the bulk-compile report) asked to scroll to a question. It
    // carries its own section/subsection from the structure walk, so there is
    // nothing to re-derive here — just page to it and hand off to the focus
    // signals the section components already watch.
    effect(() => {
      const question = this.compilingDashboardService?.questionToFocus();
      if (!question) return;

      untracked(() => {
        this.compilingDashboardService?.questionToFocus.set(undefined);

        const module = this.forms.moduleStructure();
        const sectionIndex =
          module?.root.children.indexOf(question.sectionId) ?? -1;
        if (sectionIndex === -1) return;

        this.activeView.set('form');
        this.loadAndFocus(sectionIndex, question.subSectionId);
      });
    });
  }

  protected getSlot(id: string): DocumentAttachment[] {
    return this.attachments.envelopeBAttachments()[id] ?? [];
  }

  protected setSlot(id: string, files: DocumentAttachment[]): void {
    this.attachments.envelopeBAttachments.update((v) => ({
      ...v,
      [id]: files,
    }));
  }

  readonly navItems = computed<SidebarNavItem[]>(() => {
    const module = this.forms.moduleStructure();
    if (!module) return [];

    return module.root.children
      .map((sectionId) => {
        const section = module[sectionId];
        if (!isSectionStructureObject(section)) return null;

        const children = section.children
          .map((subId) => {
            const sub = module[subId];
            if (!isSubSectionStructureObject(sub) || !sub.customization.title) {
              return null;
            }
            return {
              id: subId,
              label: sub.customization.title,
              meta: sub.customization.points
                ? `${sub.customization.points} pt`
                : undefined,
            };
          })
          .filter((s) => s !== null);

        return {
          id: sectionId,
          label: section.customization.title,
          meta: section.customization.points
            ? `${section.customization.points} pt`
            : undefined,
          children: children.length ? children : undefined,
        } satisfies SidebarNavItem;
      })
      .filter((s) => s !== null);
  });

  onPageChange(event: PaginatorState): void {
    // this.first.set(event.first ?? 0);
    this.loadAndFocus(event.page ?? 0);
  }

  onNavClick(event: SidebarNavClickEvent): void {
    if (event.item.id === DOCUMENTS_ID) {
      this.activeView.set('documents');
      return;
    }

    if (event.item.id === PREMESSA_ID) {
      this.activeView.set('premessa');
      return;
    }

    const module = this.forms.moduleStructure();
    if (!module) return;

    const sectionIndex = module.root.children.indexOf(event.item.id);
    if (sectionIndex === -1) return;

    this.activeView.set('form');
    this.loadAndFocus(sectionIndex, event.child?.id);
  }

  private loadAndFocus(sectionIndex: number, subsectionId?: string): void {
    this.first.set(sectionIndex);

    if (!subsectionId) {
      const sectionId = this.sectionId();
      this.compilingDashboardService?.sectionToFocus.set(sectionId);
    } else {
      this.compilingDashboardService?.subSectionToFocus.set(subsectionId);
    }
  }

  uploadAttachmentFn(
    category: string,
  ): (file: File) => Observable<DocumentAttachment> {
    return (file: File) => {
      console.log('Uploading file', file, 'with category', category);
      return of({
        id: 'string',
        fileName: 'string',
        url: 'string',
      } as DocumentAttachment);
    };
  }
}
